import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/client';
import { useTheme } from '../context/ThemeContext';
import './CustomerDashboard.css';

// All available AI Tools configuration
const AI_TOOLS = {
  'risk-analysis': {
    name: 'HaliCredit',
    title: 'Credit Risk Assessment',
    description: 'Analyze debtor creditworthiness, payment history and financial health to assess invoice financing risk',
    icon: '🎯',
    color: '#10b981',
    gradient: 'linear-gradient(135deg, #10b981, #059669)',
    launchPath: '/risk-analysis',
    freePlan: { id: 'risk-free', name: 'Free', quota: 10, price: 0 }
  },
  'hali-leads': {
    name: 'HaliLeads',
    title: 'Lead Generation Agent',
    description: 'AI-powered lead discovery and qualification',
    icon: '🎯',
    color: '#f59e0b',
    gradient: 'linear-gradient(135deg, #f59e0b, #d97706)',
    launchPath: '/leads',
    freePlan: { id: 'leads-free', name: 'Free', quota: 20, price: 0 }
  },
  'contract-analyzer': {
    name: 'Contract Analyzer',
    title: 'Contract Analysis Agent',
    description: 'AI-powered contract review and analysis',
    icon: '📄',
    color: '#8b5cf6',
    gradient: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
    launchPath: '/contract-analyzer',
    freePlan: { id: 'contract-free', name: 'Free', quota: 5, price: 0 }
  },
  'invoice-financing-check': {
    name: 'HaliVerify',
    title: 'Invoice Eligibility Checker',
    description: 'AI-powered verification of invoice authenticity and financing eligibility',
    icon: '📋',
    color: '#6366f1',
    gradient: 'linear-gradient(135deg, #6366f1, #4f46e5)',
    launchPath: '/invoice-financing',
    freePlan: { id: 'invoice-free', name: 'Free', quota: 10, price: 0 }
  },
  'past-trade-studio': {
    name: 'HaliPastTrade',
    title: 'Past Trade Analysis Agent',
    description: 'AI-powered analysis of historical trade documents and payment performance',
    icon: '📂',
    color: '#0ea5e9',
    gradient: 'linear-gradient(135deg, #0ea5e9, #0284c7)',
    launchPath: '/past-trade-studio',
    freePlan: { id: 'past-trade-free', name: 'Free', quota: 5, price: 0 }
  }
};

const CustomerDashboard = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [toolSubscriptions, setToolSubscriptions] = useState({});
  const [subscribingTool, setSubscribingTool] = useState(null);

  // Theme-aware colors
  const colors = {
    cardBg: isDark ? '#1e293b' : 'white',
    textPrimary: isDark ? '#f3f4f6' : '#1f2937',
    textSecondary: isDark ? '#9ca3af' : '#6b7280',
    textMuted: isDark ? '#6b7280' : '#4b5563',
    border: isDark ? '#334155' : '#e5e7eb',
    bgMuted: isDark ? '#0f172a' : '#f3f4f6'
  };

  const loadUserData = useCallback(async () => {
    try {
      setLoading(true);

      // Get email from URL first, then localStorage
      const urlParams = new URLSearchParams(window.location.search);
      let email = urlParams.get('email');
      
      if (!email) {
        email = localStorage.getItem('halisoft_email');
      }
      if (!email) {
        const storedUser = localStorage.getItem('halisoft_user');
        if (storedUser) {
          try {
            email = JSON.parse(storedUser)?.email;
          } catch (e) { /* ignore */ }
        }
      }
      // Always store email for future use
      if (email) {
        localStorage.setItem('halisoft_email', email);
      }

      // Load user info, subscription, and admin status from backend
      const [userResponse, subscriptionResponse, adminResponse] = await Promise.all([
        api.get('/api/user/me').catch(() => ({ data: { email: email || 'Anonymous' } })),
        api.get(`/api/subscriptions/current${email ? `?email=${encodeURIComponent(email)}` : ''}`).catch(() => ({ data: null })),
        api.get('/api/auth/check-admin').catch(() => ({ data: { isAdmin: false } }))
      ]);
      
      const userData = userResponse.data || { email: 'Anonymous' };

      // Check admin status
      setIsAdmin(adminResponse.data?.isAdmin || false);

      // Add subscription data to user
      if (subscriptionResponse.data) {
        userData.subscription = {
          plan: subscriptionResponse.data,
          status: subscriptionResponse.data.status || 'active'
        };
      }

      setUser(userData);

      // Load subscription status for all AI tools
      const storedEmail = email || localStorage.getItem('halisoft_email');
      if (storedEmail) {
        const toolSubs = {};
        const moduleIds = Object.keys(AI_TOOLS);

        await Promise.all(moduleIds.map(async (moduleId) => {
          try {
            const [subRes, quotaRes] = await Promise.all([
              api.get(`/api/subscriptions/current?email=${encodeURIComponent(storedEmail)}&module=${moduleId}`).catch(() => ({ data: null })),
              api.get(`/api/user/quota?email=${encodeURIComponent(storedEmail)}&module=${moduleId}`).catch(() => ({ data: null }))
            ]);
            toolSubs[moduleId] = {
              subscription: subRes.data,
              quota: quotaRes.data,
              isSubscribed: !!subRes.data || (quotaRes.data && quotaRes.data.limit > 0)
            };
          } catch (e) {
            toolSubs[moduleId] = { subscription: null, quota: null, isSubscribed: false };
          }
        }));

        setToolSubscriptions(toolSubs);
        console.log('[Dashboard] Tool subscriptions loaded:', toolSubs);
      }
    } catch (err) {
      console.error('Error loading user data:', err);
      if (err.response?.status === 401) {
        navigate('/login');
      } else {
        setError('Error loading data');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    loadUserData();
  }, [loadUserData]);

  // Subscribe to FREE plan for a tool
  const handleSubscribe = async (moduleId) => {
    const email = user?.email || localStorage.getItem('halisoft_email');
    if (!email) {
      alert('Please log in to subscribe');
      navigate('/login');
      return;
    }

    setSubscribingTool(moduleId);
    const tool = AI_TOOLS[moduleId];

    try {
      // Create free subscription
      const response = await api.post('/api/subscriptions/subscribe-free', {
        email,
        module: moduleId,
        planId: tool.freePlan.id,
        planName: tool.freePlan.name,
        quota: tool.freePlan.quota
      });

      console.log('[Dashboard] Free subscription created:', response.data);

      // Update local state to show subscribed
      setToolSubscriptions(prev => ({
        ...prev,
        [moduleId]: {
          subscription: response.data.subscription || { name: 'Free', price: 0 },
          quota: { used: 0, limit: tool.freePlan.quota, remaining: tool.freePlan.quota },
          isSubscribed: true
        }
      }));

      // Show success message and go to the tool
      alert(`✅ Successfully subscribed to ${tool.name} Free plan!`);
      navigate(`${tool.launchPath}${email ? `?email=${encodeURIComponent(email)}` : ''}`);

    } catch (err) {
      console.error('Subscribe error:', err);
      // Even if API fails, we can still let them use free tier (tracked locally)
      setToolSubscriptions(prev => ({
        ...prev,
        [moduleId]: {
          subscription: { name: 'Free', price: 0 },
          quota: { used: 0, limit: tool.freePlan.quota, remaining: tool.freePlan.quota },
          isSubscribed: true
        }
      }));
      // Still show success (free tier fallback) and go to the tool
      alert(`✅ ${tool.name} Free plan activated!`);
      navigate(`${tool.launchPath}${email ? `?email=${encodeURIComponent(email)}` : ''}`);
    } finally {
      setSubscribingTool(null);
    }
  };

  // Launch a tool
  const handleLaunchTool = (moduleId) => {
    // Try multiple sources for email
    let email = user?.email;
    if (!email || email === 'Anonymous' || email === 'Guest') {
      email = localStorage.getItem('halisoft_email');
    }
    if (!email) {
      const storedUser = localStorage.getItem('halisoft_user');
      if (storedUser) {
        try {
          email = JSON.parse(storedUser)?.email;
        } catch (e) { /* ignore */ }
      }
    }
    if (!email) {
      // Get from URL params as last resort
      const urlParams = new URLSearchParams(window.location.search);
      email = urlParams.get('email');
    }

    const tool = AI_TOOLS[moduleId];
    navigate(`${tool.launchPath}${email ? `?email=${encodeURIComponent(email)}` : ''}`);
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="spinner-large"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-error">
        <p>{error}</p>
        <button onClick={loadUserData} className="btn-retry">
          Retry
        </button>
      </div>
    );
  }

  // Count how many tools are subscribed
  const subscribedCount = Object.values(toolSubscriptions).filter(t => t.isSubscribed).length;

  return (
    <div>
          {/* Welcome Banner */}
          <div style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: '20px',
            padding: '2rem',
            marginBottom: '2rem',
            color: 'white',
            position: 'relative',
            overflow: 'hidden'
          }}>
            <div style={{ position: 'relative', zIndex: 1 }}>
              <h1 style={{ margin: 0, fontSize: '1.75rem', fontWeight: '700' }}>
                Welcome{user?.first_name ? `, ${user.first_name}` : ''}! 👋
              </h1>
              <p style={{ margin: '0.5rem 0 0', opacity: 0.9, fontSize: '1.1rem' }}>
                Subscribe to AI tools below to get started with your free plan
              </p>

              {/* Quick stats */}
              <div style={{ display: 'flex', gap: '2rem', marginTop: '1.5rem' }}>
                <div>
                  <div style={{ fontSize: '2rem', fontWeight: '700' }}>{subscribedCount}</div>
                  <div style={{ opacity: 0.8, fontSize: '0.9rem' }}>Active Tools</div>
                </div>
                <div>
                  <div style={{ fontSize: '2rem', fontWeight: '700' }}>{Object.keys(AI_TOOLS).length}</div>
                  <div style={{ opacity: 0.8, fontSize: '0.9rem' }}>Available Tools</div>
                </div>
              </div>
            </div>

            {/* Decorative circles */}
            <div style={{
              position: 'absolute',
              top: '-50px',
              right: '-50px',
              width: '200px',
              height: '200px',
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.1)'
            }} />
            <div style={{
              position: 'absolute',
              bottom: '-30px',
              right: '100px',
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.1)'
            }} />
          </div>

          {isAdmin && (
            <div style={{ marginBottom: '1.5rem' }}>
              <button
                onClick={() => navigate('/admin')}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: 'linear-gradient(135deg, #f43f5e, #e11d48)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                👨‍💼 Go to Admin Dashboard
              </button>
            </div>
          )}

          {/* AI Tools Section */}
          <div className="ai-tools-section" style={{ marginBottom: '2rem' }}>
            <h2 style={{ marginBottom: '1.5rem', fontSize: '1.5rem', fontWeight: '700', color: colors.textPrimary }}>
              🤖 Available AI Tools
            </h2>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '1.5rem'
            }}>
              {Object.entries(AI_TOOLS).map(([moduleId, tool]) => {
                const toolData = toolSubscriptions[moduleId] || {};
                const isSubscribed = toolData.isSubscribed;
                const isSubscribing = subscribingTool === moduleId;
                const toolQuota = toolData.quota;

                return (
                  <div
                    key={moduleId}
                    role="button"
                    tabIndex={0}
                    onClick={() => isSubscribed ? handleLaunchTool(moduleId) : handleSubscribe(moduleId)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        isSubscribed ? handleLaunchTool(moduleId) : handleSubscribe(moduleId);
                      }
                    }}
                    style={{
                      background: colors.cardBg,
                      borderRadius: '16px',
                      padding: '1.5rem',
                      boxShadow: isDark ? '0 4px 12px rgba(0,0,0,0.3)' : '0 4px 12px rgba(0,0,0,0.08)',
                      border: `2px solid ${isSubscribed ? tool.color + '40' : colors.border}`,
                      transition: 'all 0.3s ease',
                      cursor: 'pointer'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.boxShadow = isDark ? '0 12px 24px rgba(0,0,0,0.4)' : '0 12px 24px rgba(0,0,0,0.12)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = isDark ? '0 4px 12px rgba(0,0,0,0.3)' : '0 4px 12px rgba(0,0,0,0.08)';
                    }}
                  >
                    {/* Header */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
                      <div style={{
                        width: '56px',
                        height: '56px',
                        borderRadius: '14px',
                        background: tool.gradient,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1.75rem',
                        boxShadow: `0 4px 12px ${tool.color}40`
                      }}>
                        {tool.icon}
                      </div>
                      <div>
                        <h3 style={{ margin: 0, fontSize: '1.2rem', fontWeight: '700', color: colors.textPrimary }}>
                          {tool.name}
                        </h3>
                        <p style={{ margin: 0, fontSize: '0.85rem', color: colors.textSecondary }}>
                          {tool.title}
                        </p>
                      </div>
                    </div>

                    {/* Description */}
                    <p style={{ color: colors.textMuted, fontSize: '0.9rem', marginBottom: '1rem', minHeight: '40px' }}>
                      {tool.description}
                    </p>

                    {/* Subscription Status */}
                    {isSubscribed && (
                      <div style={{
                        background: `${tool.color}10`,
                        borderRadius: '10px',
                        padding: '0.75rem 1rem',
                        marginBottom: '1rem'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                          <span style={{ fontSize: '0.8rem', color: tool.color, fontWeight: '600' }}>
                            ✓ {toolData.subscription?.name || 'Free'} Plan
                          </span>
                          <span style={{ fontSize: '0.8rem', color: colors.textSecondary }}>
                            {toolQuota?.used || 0} / {toolQuota?.limit || tool.freePlan.quota} used
                          </span>
                        </div>
                        <div style={{ height: '6px', background: isDark ? '#334155' : '#e5e7eb', borderRadius: '3px', overflow: 'hidden' }}>
                          <div style={{
                            width: `${Math.min(((toolQuota?.used || 0) / (toolQuota?.limit || tool.freePlan.quota)) * 100, 100)}%`,
                            height: '100%',
                            background: tool.gradient,
                            borderRadius: '3px'
                          }} />
                        </div>
                      </div>
                    )}

                    {/* Free Plan Info (when not subscribed) */}
                    {!isSubscribed && (
                      <div style={{
                        background: colors.bgMuted,
                        borderRadius: '10px',
                        padding: '0.75rem 1rem',
                        marginBottom: '1rem'
                      }}>
                        <div style={{ fontSize: '0.85rem', color: colors.textMuted }}>
                          <strong style={{ color: '#059669' }}>FREE</strong> - {tool.freePlan.quota} {moduleId === 'contract-analyzer' ? 'analyses' : moduleId === 'invoice-financing-check' ? 'checks' : 'credits'}/month
                        </div>
                      </div>
                    )}

                    {/* Action Button */}
                    {isSubscribed ? (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleLaunchTool(moduleId); }}
                        style={{
                          width: '100%',
                          padding: '0.9rem 1.5rem',
                          background: tool.gradient,
                          color: 'white',
                          border: 'none',
                          borderRadius: '10px',
                          fontWeight: '700',
                          fontSize: '1rem',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '0.5rem'
                        }}
                      >
                        🚀 Launch {tool.name}
                      </button>
                    ) : (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleSubscribe(moduleId); }}
                        disabled={isSubscribing}
                        style={{
                          width: '100%',
                          padding: '0.9rem 1.5rem',
                          background: isSubscribing ? '#9ca3af' : tool.gradient,
                          color: 'white',
                          border: 'none',
                          borderRadius: '10px',
                          fontWeight: '700',
                          fontSize: '1rem',
                          cursor: isSubscribing ? 'wait' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '0.5rem'
                        }}
                      >
                        {isSubscribing ? (
                          <>⏳ Subscribing...</>
                        ) : (
                          <>✨ Subscribe Free</>
                        )}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="actions-section">
            <h3>Quick Actions</h3>
            <div className="actions-grid">
              <button onClick={() => {
                const e = user?.email || localStorage.getItem('halisoft_email') || '';
                navigate(`/subscription${e ? `?email=${encodeURIComponent(e)}` : ''}`);
              }} className="action-card">
                <span className="action-icon">💳</span>
                <div className="action-content">
                  <strong>Manage Subscription</strong>
                  <p>Upgrade, downgrade, or cancel</p>
                </div>
              </button>

              <button onClick={() => {
                const e = user?.email || localStorage.getItem('halisoft_email') || '';
                navigate(`/invoices${e ? `?email=${encodeURIComponent(e)}` : ''}`);
              }} className="action-card">
                <span className="action-icon">📄</span>
                <div className="action-content">
                  <strong>My Invoices</strong>
                  <p>View payment history</p>
                </div>
              </button>

              <button onClick={() => {
                const e = user?.email || localStorage.getItem('halisoft_email') || '';
                navigate(`/settings${e ? `?email=${encodeURIComponent(e)}` : ''}`);
              }} className="action-card">
                <span className="action-icon">⚙️</span>
                <div className="action-content">
                  <strong>Settings</strong>
                  <p>Manage my account</p>
                </div>
              </button>

              <button onClick={() => window.open('/docs', '_blank')} className="action-card">
                <span className="action-icon">📚</span>
                <div className="action-content">
                  <strong>Documentation</strong>
                  <p>API & user guides</p>
                </div>
              </button>
            </div>
          </div>

          {/* Getting Started / Status */}
          <div className="activity-section">
            <h3>Getting Started</h3>
            <div className="activity-list">
              {subscribedCount > 0 ? (
                <>
                  <div className="activity-item">
                    <span className="activity-icon">✅</span>
                    <div className="activity-content">
                      <strong>{subscribedCount} tool{subscribedCount > 1 ? 's' : ''} active</strong>
                      <p>You can launch your subscribed tools above</p>
                    </div>
                  </div>
                  <div className="activity-item">
                    <span className="activity-icon">💡</span>
                    <div className="activity-content">
                      <strong>Upgrade anytime</strong>
                      <p>Go to Subscription to see premium plans</p>
                    </div>
                  </div>
                </>
              ) : (
                <div className="no-activity">
                  <span>👆</span>
                  <p>Subscribe to any tool above to get started</p>
                  <p style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '0.5rem' }}>
                    All tools include a free plan with limited usage
                  </p>
                </div>
              )}
            </div>
          </div>
    </div>
  );
};

export default CustomerDashboard;
